<template>
  <main class="relative duration" :style="{ top: !y ? 0 : anchorOffset, marginBottom: anchorOffset }">
    <nav class="sticky top-0 z-1 py-2 lg:hidden" style="background-color: #bbdf32">
      <ul class="flex center px-5 between" style="column-gap: 3rem">
        <li>
          <base-button :aria-label="$t.scrollToTop" @click="scrollToTop">
            <base-picture v-bind="nav.icon" class="w-10" />
          </base-button>
        </li>
        <li>
          <base-button
            aria-controls="mobile menu"
            :aria-expanded="showMenuRef"
            :aria-label="$t.toggleMobileMenu"
            @click="toggleMenu"
          >
            <vf-icon class="c-brand-1" name="menu" size="xl" />
          </base-button>
        </li>
      </ul>
    </nav>
    <!-- Mobile menu -->
    <transition
      enter-active-class="transform ease-in-out"
      enter-from-class="translate-x-75"
      leave-active-class="transform ease-in-out"
      leave-to-class="translate-x-75"
      @after-enter="activateFocusTrap"
      @after-leave="deactivateFocusTrap"
    >
      <div
        v-if="showMenuRef"
        ref="mobileMenuRef"
        class="fixed right-0 top-0 z-overlay h-screen px-8 pt-18 duration lg:hidden"
        style="width: 19rem; background-color: #41ad49"
      >
        <base-button
          aria-controls="mobile menu"
          :aria-expanded="showMenuRef"
          :aria-label="$t.close"
          class="absolute right-0 top-0 mr-5 mt-5 b-2 b-white"
          @click="toggleMenu"
        >
          <vf-icon class="c-white" name="close" size="lg" />
        </base-button>
        <ul class="f-col gap-y-5">
          <li v-for="({ text, to }, key) in nav.items" :key>
            <base-link
              class="subtitle-2 c-white uppercase"
              :to
              @click="toggleMenu()"
            >
              {{ text }}
            </base-link>
          </li>
        </ul>
      </div>
    </transition>
    <!-- Hero section -->
    <base-picture v-bind="mainBg" class="absolute-0 -z-1" />
    <div v-style:h="{ sm: 'auto', lg: '75vh' }" class="relative">
      <base-picture v-bind="hero.background" class="absolute-0 -z-1" />
      <div
        v-style="{
          'p': { sm: '3.75rem 1.25rem', lg: 'initial' },
          'text-size': { sm: '3.1rem', lg: '6.25rem' },
        }"
        class="h-full f-col center"
      >
        <base-picture v-bind="hero.icon" class="mb-5 <lg:w-20" />
        <h1 class="ws-pre-wrap text-center c-white fw-bold lh-none font-secondary uppercase">
          {{ hero.heading }}
        </h1>
      </div>
    </div>
    <!-- Shop all section -->
    <div
      v-style:p="{ sm: '5.25rem 0.625rem', md: '3.25rem 1rem', lg: '3.25rem 1rem 6.375rem' }"
      id="shop-all"
      class="mx-a w-fit text-center"
      :style="`scroll-margin-top: ${anchorOffset}`"
    >
      <base-link :to="shopCollection.link">
        <base-picture v-bind="shopCollection.image" />
      </base-link>
      <h2
        class="fw-bold lh-none font-secondary uppercase"
        style="font-size: clamp(2.25rem, 5vw, 3.75rem); margin: 3rem auto clamp(1rem, 5vw, 2rem)"
      >
        {{ shopCollection.heading }}
      </h2>
      <p
        v-style:px="{ sm: '1rem', lg: '8rem' }"
        v-html="shopCollection.body"
        class="mb-8"
        style="font-size: clamp(1.25rem, 2vw, 1.5rem)"
      />
      <vf-button
        :to="shopCollection.link"
      >
        {{ shopCollection.button }}
      </vf-button>
    </div>
    <!-- Technology section -->
    <div
      id="technology"
      :style="`
        background: url(${qualification.background}) no-repeat top left/cover;
        padding-bottom: 4.4rem;
        scroll-margin-top: ${anchorOffset}
      `"
    >
      <div class="mx-a" style="padding: 4.4rem 1.25rem; max-width: 86.25rem">
        <div class="px-2 md:px-5">
          <div class="mx-a mb-12 flex items-center gap-8 <md:flex-col" style="max-width: 56.25rem">
            <base-picture v-bind="qualification.headingImage" />
            <p
              v-style:text-size="{ sm: '2.8rem', md: '2rem' }"
              class="fw-bold lh-none font-secondary uppercase <md:text-center"
            >
              {{ qualification.heading }}
            </p>
          </div>
          <ul class="mb-8 flex gap-8 <lg:flex-col">
            <li
              v-for="({ heading, body }, key) of qualification.threeR"
              :key
              class="flex-1 bg-brand-1 p-8 c-white"
            >
              <h3 class="mb-3 text-4xl fw-bold font-secondary uppercase">
                {{ heading }}
              </h3>
              <p class="text-base">
                {{ body }}
              </p>
            </li>
          </ul>
          <p class="mb-12 text-base italic" style="color: #231f20">
            {{ qualification.disclaimer }}
          </p>
        </div>

        <div v-style:p="{ sm: '3.75rem 0 0', md: '4.375rem 0 0' }">
          <h2
            v-style="{
              'lh': { sm: 1, lg: 0.7 },
              'text-size': { sm: '2.625rem', lg: '4.6875rem' },
            }"
            class="mx-a mb-8 w-fit px-5 py-3 text-center c-brand-1 fw-bold font-secondary uppercase"
            style="background-color: #bbdf32"
          >
            {{ technology.heading }}
          </h2>
          <ul v-style:cols="{ lg: 'repeat(3, minmax(0, 1fr))' }" class="grid gap-8">
            <li
              v-for="({ heading, image, body }, key) of technology.list"
              :key
              class="bg-white p-8"
              :style="`background: url(${technology.listItemBackground}) no-repeat center/cover`"
            >
              <h3
                v-style:text-size="{ sm: '1.9rem', md: '2.3rem' }"
                class="mb-5 break-words text-center fw-bold lh-none font-secondary uppercase"
              >
                {{ heading }}
              </h3>
              <base-picture v-bind="image" class="w-full" />
              <p v-html="body" class="mt-5 text-base" style="color: #231f20" />
            </li>
          </ul>
        </div>
      </div>
    </div>
    <!-- Our Goals section -->
    <div
      id="our-goals"
      class="mx-a px-5 py-15"
      style="max-width: 86.25rem"
      :style="`scroll-margin-top: ${anchorOffset}`"
    >
      <h2
        v-style="{
          'lh': { sm: 1, lg: 0.7 },
          'text-size': { sm: '2.625rem', lg: '4.6875rem' },
        }"
        class="mx-a mb-8 w-fit px-5 py-3 text-center c-brand-1 fw-bold font-secondary uppercase"
        style="background-color: #bbdf32"
      >
        {{ goals.heading }}
      </h2>
      <p
        v-for="({ paragraph }, key) in goals.body"
        :key
        class="mx-a mb-12 max-w-full text-center md:max-w-2/3"
        style="color: #231f20"
      >
        {{ paragraph }}
      </p>
      <ul v-style:cols="{ lg: 'repeat(3, minmax(0, 1fr))' }" class="grid gap-8 c-white">
        <li v-for="({ image, upperCopy, lowerCopy }, key) in goals.tiles" :key>
          <div class="px-8 py-10 text-center" style="background-color: #bbdf32; mix-blend-mode: multiply">
            <base-picture v-bind="image" class="mx-a mb-5" />
            <p class="px-5 c-brand-1 fw-bold">
              {{ upperCopy }}
            </p>
          </div>
          <div v-html="lowerCopy" class="bg-brand-1 p-8" />
        </li>
      </ul>
    </div>
    <!-- Footer sticky nav -->
    <ul class="sticky bottom-0 z-1 flex center py-2 <lg:hidden" style="background-color: #bbdf32; column-gap: 3rem">
      <li>
        <base-button :aria-label="$t.scrollToTop" @click="scrollToTop">
          <base-picture v-bind="nav.icon" class="w-10" />
        </base-button>
      </li>
      <li v-for="({ text, to }, key) in nav.items" :key>
        <base-link class="text-lg @hactive:c-[#41ad49] c-brand-1 fw-bold font-secondary uppercase duration" :to>
          {{ text }}
        </base-link>
      </li>
    </ul>
  </main>
</template>

<script lang="ts" setup>
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

const { lock, unlock } = useBodyScroll()
const header = useHeaderStore()
const { $t } = useNuxtApp()
const { y } = useScroll(document)
const { data, error } = await useApi().internal.getStaticPage('sustainability')
if (!data.value) throw createError({ ...error.value, fatal: true })

const { nav, hero, qualification, mainBg, shopCollection, technology, goals } = data.value

const mobileMenuRef = ref<HTMLDivElement>()
const showMenuRef = ref(false)

const anchorOffset = computed(() => pxToRem(header.sticky.margin > 0 ? 0 : -header.height.header))

const toggleMenu = () => {
  showMenuRef.value = !showMenuRef.value
}

const closeMenu = () => showMenuRef.value = false

const scrollToTop = () => {
  scrollTo({ top: 0, behavior: 'smooth' })
}

const { activate, deactivate } = useFocusTrap(mobileMenuRef, {
  clickOutsideDeactivates: true,
  initialFocus: false,
  onDeactivate: closeMenu
})

const activateFocusTrap = () => {
  activate()
  lock()
}

const deactivateFocusTrap = () => {
  deactivate()
  unlock()
}
</script>
